import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, FormArray, FormBuilder } from '@angular/forms';
import { Router } from '@angular/router';
import { Select, Store } from '@ngxs/store';
import { isEqual, isNil } from 'lodash';
import { combineLatest, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, pairwise, takeUntil } from 'rxjs/operators';
import { QuestionSubmission } from 'src/app/quiz/models/question-submission.model';
import { LabelQuestion, QuizLabelOption } from 'src/app/quiz/models/quiz-question.model';
import { QuizActions } from 'src/app/quiz/state/quiz.actions';
import { QuizState } from 'src/app/quiz/state/quiz.state';
import { LabelModel } from 'src/app/story/models/labels/label.model';
import { StoryLabelsState } from 'src/app/story/state/labels/story.labels.state';

@Component({
  selector: 'app-quiz-labels',
  templateUrl: './quiz-labels.component.html',
  styleUrls: ['./quiz-labels.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class QuizLabelsComponent implements OnInit, OnDestroy {

  @Select(StoryLabelsState.getLabelsCollapsed)
  labelsCollapsed$: Observable<boolean>;
  
  @Select(QuizState.getActiveQuestionLabels)
  quizLabels$: Observable<QuizLabelOption[]>;

  @Select(QuizState.getQuizModeActive)
  quizModeActive$: Observable<boolean>;

  @Select(QuizState.getAllSubmissions)
  allSubmissions$: Observable<QuestionSubmission[]>;

  @Select(QuizState.getActiveSubmission)
  activeSubmission$: Observable<QuestionSubmission>;

  form: FormGroup;
  optionsArray: FormArray;
  activeSubmitted: QuestionSubmission;

  private ngUnsubscribe = new Subject();

  mode: 'edit' | 'view' | 'summary' = 'edit';

  constructor(
    private store: Store,
    private formBuilder: FormBuilder,
    private changeRef: ChangeDetectorRef,
    private router: Router
  ) {
    this.form = this.formBuilder.group({
      options: this.formBuilder.array([])
    });
    this.optionsArray = this.form.get('options') as FormArray;
  }

  ngOnInit() {
    this.initFormListeners();
    this.initQuizSubmissionListeners();
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  initFormListeners() {
    // build label form elements
    this.quizLabels$
      .pipe(
        // rebuild when ordering changes
        distinctUntilChanged((a, b) => {
          const aValues = a.map((a1) => a1.value);
          const bValues = b.map((b1) => b1.value);
          return isEqual(aValues, bValues);
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((options) => {
        // build options + set initial
        const quizModeActive = this.store.selectSnapshot(QuizState.getQuizModeActive);
        this.buildFormOptions(options, quizModeActive);

        this.changeRef.markForCheck();
      });

    // enable/disable form
    combineLatest([
      this.quizModeActive$,
      this.quizLabels$
    ])
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(([quizModeActive]) => {
        this.setFormEnabled(quizModeActive);
        this.changeRef.markForCheck();
      });
  }

  initQuizSubmissionListeners() {
    // sync active submission -> quiz labels form
    this.activeSubmission$
      .pipe(
        filter((activeSubmission) => !isNil(activeSubmission) && !isNil(activeSubmission.answer) && Array.isArray(activeSubmission.answer)),
        distinctUntilChanged((a, b) => isEqual(a, b)),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((activeSubmission) => {
        const formAnswers = this.form.value.options;
        const submittedAnswers = activeSubmission.answer;
        if (!isEqual(formAnswers, submittedAnswers)) {
          this.form.patchValue({
            options: submittedAnswers
          }, { emitEvent: false, onlySelf: true });
          this.changeRef.markForCheck();
        }
        
      });

    // sync quiz labels form -> active submission
    this.form.valueChanges
      .pipe(
        distinctUntilChanged((a, b) => isEqual(a, b)),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((value) => {
        const questionId = this.store.selectSnapshot(QuizState.getActiveQuestionId);
        const submission = this.store.selectSnapshot(QuizState.getActiveSubmission);

        const submissionAnswers = !isNil(submission) ? submission.answer : [];
        const formAnswers: string[] = value.options;

        if (this.store.selectSnapshot(QuizState.getAllSubmissions).some(q => q.questionId === questionId)) {
          return;
        }

        if (!isEqual(formAnswers, submissionAnswers)) {
          this.store.dispatch(new QuizActions.SetActiveSubmission({
            questionId: questionId,
            answer: formAnswers
          }));
        }
        this.changeRef.markForCheck();
      });

    // track if submitting or submitted
    combineLatest([
      this.quizModeActive$,
      this.allSubmissions$
    ])
      .subscribe(([active, submissions]) => {
        const questionId = this.store.selectSnapshot(QuizState.getActiveQuestionId);
        this.activeSubmitted = submissions
          .find((submission) => submission.questionId === questionId);


        const isEdit = this.router.url.includes('/edit');
        if (isEdit) {
          this.mode = 'edit';
        } else {
          this.mode = active && !isNil(this.activeSubmitted) ? 'summary' : 'view';
        }


        this.changeRef.markForCheck();
      });
  }

  isCorrect(option: QuizLabelOption, submissions: QuestionSubmission, index: number): boolean {
    if (!isNil(submissions)) {
      const correctAnswer = option.value;
      const submittedAnswer = submissions.answer[index];
      return submittedAnswer === correctAnswer;
    } else {
      return undefined;
    }
  }

  handleLabelUpdated(label: LabelModel, index: number) {
    const question = this.store.selectSnapshot(QuizState.getActiveQuestion) as LabelQuestion;
    // update selected question label
    question.data.options[index].label = label;
    // update question
    this.store.dispatch(new QuizActions.SetQuestion(question));
  }

  private buildFormOptions(options: QuizLabelOption[], emptySelected: boolean) {
    this.optionsArray.clear();
    options.forEach((option) => {
      this.optionsArray.push(
        this.formBuilder.control(emptySelected ? '' : option.value)
      );
    });
  }

  private setFormEnabled(enabled: boolean) {
    if (enabled) {
      this.optionsArray.enable();
    } else {
      this.optionsArray.disable();
    }
  }

}
